import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
  answerBlock
} from 'actions';

import Question from 'components/Questionnaires/Question';
import { QuestionCheckBoxField, QuestionTextField } from 'components/Questionnaires/fields';
import { requiredValidator, validateQuestion } from 'components/Questionnaires/validators';

export const QuestionFtinBlock = ({ block, onAnswer }) => {
  const dispatch = useDispatch();
  const [answer, setAnswer] = useState(block?.answers ?? { appliedFor: '' });

  const validators = {
    conditions: [
      { condition: requiredValidator, message: 'questionnaire.foreignTin.errorMessage' }
    ]
  };

  const [value, setValue] = useState({
    id: 'value',
    label: 'questionnaire.foreignTin',
    component: QuestionTextField,
    info: {
      instructions: 'questionnaire.foreignTin.info'
    },
    visible: true,
    validation: block.componentProps?.required === true ? validators : [],
    answer: answer?.value,
  });

  const ftinNotRequired = {
    id: 'ftinNotRequired',
    component: QuestionCheckBoxField,
    componentProps:
    {
      option: 'questionnaire.taxIdentificationNumbers.foreignTin.notRequired.label'
    },
    visible: block.componentProps?.showFtinNotRequired !== undefined ? block.componentProps?.showFtinNotRequired : true,
    answer: answer?.ftinNotRequired ?? undefined
  };

  const blockIdentifier = useMemo(() => ((block && block.id) ? `${ block.id }${ block.index !== undefined ? (`.${ block.index }`) : '' }` : undefined), [block.index]);

  const isValid = () => {
    if (block.componentProps?.showFtinNotRequired === false) {
      return value.isValid;
    }
    if (answer.value === undefined && (ftinNotRequired.answer === '' || ftinNotRequired.answer === undefined)) {
      return undefined;
    }
    return value.isValid || (!answer.value && answer.ftinNotRequired === 'true');
  };

  const onQuestionChange = (q, v) => {
    switch (q.id) {
      case 'value': {
        const newValue = { ...q, answer: v };
        validateQuestion(newValue);
        setValue(newValue);
        setAnswer({ ...answer, value: v });
        break;
      }
      case 'ftinNotRequired': {
        let newValidators = [];
        if (block.componentProps?.required === true) {
          newValidators = v === 'true' ? [] : validators;
        }
        const questionProps = v === 'true' ? { isValid: true, errorMessages: undefined, disabled: true } : { disabled: false };
        const newValue = { ...value, ...questionProps, ...{ validation: newValidators } };
        validateQuestion(newValue);
        setValue(newValue);
        setAnswer({ ...answer, ftinNotRequired: v });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    onQuestionChange(value, value?.answer);
    onQuestionChange(ftinNotRequired, ftinNotRequired?.answer);
  }, [block.componentProps?.required]);

  useEffect(() => {
    if (!onAnswer || typeof onAnswer !== 'function') {
      if (Object.keys(answer).length > 0 && answer.constructor === Object) {
        dispatch(answerBlock(block.id, answer, isValid()));
      } else {
        onQuestionChange(value, undefined);
        onQuestionChange(ftinNotRequired, undefined);
      }
    } else {
      onAnswer(block, answer, isValid());
    }
    dispatch(answerBlock(block.id, answer, isValid()));
  }, [answer]);

  useEffect(() => {
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  useEffect(() => {
    if (block.isForceEdited === true) {
      onQuestionChange({ ...value, isForceEdited: true }, answer?.value ?? '');
    }
  }, [block.isForceEdited]);

  return (<React.Fragment>
    <Question key={ value.id } blockId={ blockIdentifier } question={ value } { ...value.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ ftinNotRequired.id } blockId={ blockIdentifier } question={ ftinNotRequired } { ...ftinNotRequired.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
  </React.Fragment>);
};

QuestionFtinBlock.displayName = 'QuestionFtinBlock';

QuestionFtinBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func,
};

QuestionFtinBlock.defaultProps = {
  onAnswer: null
};

export default QuestionFtinBlock;
